import {useEffect, useRef, useState} from 'react';

import {useThemeColors} from '@nfq/colors';
import {useScroll} from 'motion/react';

import type {SequenceBlockType} from 'types/payload-types';

/**
 * Custom hook for managing sequence block functionality including scroll animations and line positioning.
 * This hook handles the visual timeline elements and their positioning calculations based on the content items.
 * It provides refs for timeline elements and calculates dynamic line start/end positions based on item heights.
 *
 * @param contents The sequence block contents array from Payload CMS containing the timeline items.
 * @returns Object containing theme colors, element refs, line positioning values, and scroll progress.
 *
 * @example
 * ```tsx
 * const {
 *     colors,
 *     firstItemRef,
 *     lastItemRef,
 *     lineEnd,
 *     lineRef,
 *     lineStart,
 *     scrollYProgress
 * } = useSequenceBlock(sequenceBlockData.contents);
 * ```
 */
export const useSequenceBlock = (contents: SequenceBlockType['contents']) => {
    const colors = useThemeColors();
    const firstItemRef = useRef<HTMLLIElement>(null);
    const lastItemRef = useRef<HTMLLIElement>(null);
    const lineRef = useRef<HTMLDivElement>(null);
    const {scrollYProgress} = useScroll({
        offset: ['0 0.8', '1 0.8'],
        target: lineRef
    });
    const [lineStart, setLineStart] = useState(0);
    const [lineEnd, setLineEnd] = useState(0);

    useEffect(() => {
        // This effect can be used to handle any side effects related to the refs, such as scrolling into view
        if (firstItemRef.current) {
            const {height} = firstItemRef.current.getBoundingClientRect();

            // eslint-disable-next-line @nfq/no-magic-numbers
            setLineStart((height - 100) / 2);
        }
        if (lastItemRef.current) {
            const {height} = lastItemRef.current.getBoundingClientRect();

            // eslint-disable-next-line @nfq/no-magic-numbers
            setLineEnd(((height - 100) / 2) + 100);
        } else if (firstItemRef.current) {
            const {height} = firstItemRef.current.getBoundingClientRect();

            // eslint-disable-next-line @nfq/no-magic-numbers
            setLineEnd(((height - 100) / 2) + 100);
        }
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    }, [contents?.length]);

    return {
        colors,
        firstItemRef,
        lastItemRef,
        lineEnd,
        lineRef,
        lineStart,
        scrollYProgress
    };
};